import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { CollectionModel } from './models/collection.model';
import { catchError, map } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { BookmarkModel } from '../bookmarks/models/bookmark.model';
import { CollectionContentModel } from './models/collection.content.model';
import { OrganizationAttributeModel } from './models/organization-attribute.model';
import { BookmarksService } from '../bookmarks/bookmarks.service';
import { OrganizationUserModel } from './models/organization-user.model';
import { isNil } from 'lodash';
import { normalizeUserRole } from '../auth/models/permissions/permissions';
import { OrganizationModel } from './models/organization.model';
import { OrganizationUpdateModel } from './models/organization-update';
import { OrganizationProfileRequest, OrganizationProfileResponse } from './models/organization-profile.model';

export interface OrganizationSearchModel {
  group_id: number;
  user_id?: number;
  role_id?: number;
  q?: string;
  page_size?: number;
  offset?: number;
  sort_order?: 'user_email' | 'user_last_name' | 'user_joined_date';
}

export interface OrganizationInviteResponse {
  email: string;
  reason: string;
  status: 'success' | 'error';
}

export interface OrganizationUserRole {
  roleId: number;
  userId: number;  
}

export interface UserContentTransfer {
  sourceUserId: number;
  targetUserId?: number;
}

/**
 * Collections service endpoint
 */
const collectionsURL = '/ws/organization/collection';

@Injectable({
  providedIn: 'root'
})
export class OrganizationsService {

  constructor(
    private http: HttpClient,
    private bookmarkService: BookmarksService
  ) { }

  createOrganization(organization: OrganizationUpdateModel): Observable<any> {
    return this.http.post('/ws/organization/create', organization);
  }

  updateOrganization(organization: OrganizationUpdateModel): Observable<any> {
    return this.http.post('/ws/organization/update', organization);
  }

  getOrganizationUsers(searchParams: OrganizationSearchModel): Observable<OrganizationUserModel[]> {
    // build search params
    const qs = Object.entries(searchParams)
      .filter(([key, value]) => !isNil(value))
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    return this.http.get(`/ws/organization/users?${qs}`)
      .pipe(
        map((response: any) => response.users),
        // note: normalize user roles
        map((users: OrganizationUserModel[]) => {
          return users.map((user) => {
            return {
              ...user,
              user_role: normalizeUserRole(user.user_role)
            };
          });
        })
      );
  }

  sendOrganizationInvites(group_eid: string, invite_emails: string[]): Observable<OrganizationInviteResponse[]> {
    return this.http.post('/ws/organization/invite', {
      'group_eid': group_eid,
      'invite_emails': JSON.stringify(invite_emails)
    })
      .pipe(
        map((response: any) => {
          const { messages } = response.results_summary;
          return JSON.parse(messages);
        })
      );
  }

  getCollections(groupId: number): Observable<CollectionModel[]> {
    return this.http.get(`${collectionsURL}?group_id=${groupId}`)
      .pipe(
        map((results: Object[]) => {
          return plainToClass(CollectionModel, results)
            .map((collection) => this.normalizeCollectionModel(collection));
        })
      );
  }

  getCollectionsByContent(
    groupId: number,
    content: CollectionContentModel,
    includeContent: boolean = false,
    collectionId?: number
  ): Observable < CollectionModel[] > {
    if (includeContent && !collectionId) {
      throw new Error('collectionId must be included when includeContent is set to true');
    }

    const { content_type, content_id } = content;
    let url = `${collectionsURL}?group_id=${groupId}&content_type=${content_type}&content_id=${content_id}&include_content=${includeContent ? 1 : 0}`;
    if (includeContent) {
      url = `${url}&collection_id=${collectionId}`;
    }

    return this.http.get(url)
      .pipe(
        map((results: Object[]) => {
          return plainToClass(CollectionModel, results)
            .map((collection) => this.normalizeCollectionModel(collection));
        })
      );
  }

  /**
   * Gets the collection content id by content
   */
  getCollectionContentId(collection: CollectionModel, content: CollectionContentModel): Observable<number> {
    const { content_type, content_id } = content;
    return this.getCollectionsByContent(collection.group_id, content, true, collection.collection_id)
      .pipe(
        // restrict to target collection
        map((collections) => collections.find((c) => c.collection_id === collection.collection_id)),
        map((c) => {
          let foundBookmark: BookmarkModel;
          if (c) {
            foundBookmark = c.collection_content.find((bookmark) => {
              return content_type === 'module' ? content_id === bookmark.module_name : content_id === bookmark.id;
            });
          }
          return foundBookmark ? foundBookmark.collection_content_id : null;
        })
      );
  }

  /**
   * Gets an individual group collection.
   */
  getCollection(groupId: number, collectionId: number, includeContent: boolean): Observable<CollectionModel> {
    return this.http.get(`${collectionsURL}?group_id=${groupId}&collection_id=${collectionId}&include_content=${includeContent ? 1 : 0}`)
      .pipe(
        map((collections: CollectionModel[]) => {
          const collection = collections[0];
          return collection ? this.normalizeCollectionModel(collection) : collection;
        })
      );
  }

  /**
   * Gets the list of models in the specified collection.
   */
  getCollectionContent(groupId: number, collectionId: number, query = ''): Observable<BookmarkModel[]> {
    const queryParts = query.toLocaleLowerCase().split(' ');

    return this.getCollection(groupId, collectionId, true)
      .pipe(
        map((result: CollectionModel) => {
          // exclude unavailable
          return result.collection_content
            .map((bookmark) => plainToClass(BookmarkModel, bookmark))
            .filter((bookmark: BookmarkModel) => {
              let include = true;
              
              if (queryParts.length && bookmark.title) {
                queryParts.forEach(queryPart => {
                  include = include && bookmark.title.toLocaleLowerCase().indexOf(queryPart) >= 0;
                });
              }

              include = include && bookmark.unavailable !== true;
              return include;
            });
        })
      );
  }

  addToCollection(collection: CollectionModel, models: CollectionContentModel[]): Observable<CollectionModel[]> {
    return this.formPost<CollectionModel[]>(collectionsURL, {
      group_id: collection.group_id,
      collection_id: collection.collection_id,
      add_content: models
    })
  }

  removeFromCollection(collection: CollectionModel, collectionContentIds: number[]): Observable<CollectionModel[]> {
    return this.formPost<CollectionModel[]>(collectionsURL, {
      group_id: collection.group_id,
      collection_id: collection.collection_id,
      remove_content: collectionContentIds
    })
    .pipe(
      map((_collections) => {
        return _collections.map((_collection) => this.normalizeCollectionModel(_collection));
      })
    );
  }

  getOrganizationAttribute(attributes: OrganizationAttributeModel[], type: string, name: string): any {
    return attributes.find((attribute) => attribute.type === type && attribute.name === name);
  }

  getPublicProfile(request: OrganizationProfileRequest): Observable<OrganizationProfileResponse> {
    return this.http.get(`/ws/organization/public/profile`, { params: { ...request } })
      .pipe(
        map((results: Object) => results as OrganizationProfileResponse)
      );
  }

  /**
   * Adds the current user to the specified organization (by organization's eid)
   */
  addUserToOrganization(group_eid: string) {
    return this.http.post(`/ws/organization/user/add`, { 'group_eid': group_eid });
  }

  addUserWithGroupCode(group_code: string): Observable<string> {
    return this.http.post(`/ws/organization/user/add`, { 'group_code': group_code })
      .pipe(
        map((response: any) => response.organization_name)
      );
  }

  updateUserRoles(groupId: number, users: OrganizationUserRole[]) {
    return this.http.post('/ws/organization/users', {
      group_id: groupId,
      update_users: users.map((user)=> {
        return {
          user_id: user.userId,
          role_id: user.roleId
        };
      })
    })
    .pipe(
      map(()=> true)
    )
  }

  removeUsers(groupId: number, users: UserContentTransfer[]): Observable<boolean> {
    return this.http.post('/ws/organization/users', {
      group_id: groupId,
      remove_users: users
        .map((user) => this.createTransferRequest(user))
    })
      .pipe(
        map(() => true)
      );
  }

  transferUserContent(groupId: number, users: UserContentTransfer[]) {
    return this.http.post('/ws/organization/bookmark/transfer', {
      // group id of users
      group_id: groupId,
      // list of transfers
      transfer_bookmarks: users
        .map((transfer) => this.createTransferRequest(transfer))
    });
  }

  createTransferRequest(transfer: UserContentTransfer): any {
    const { sourceUserId, targetUserId } = transfer;

    // add target user: optional
    if (typeof (targetUserId) === 'number') { // optional: transfer to target
      return {
        user_id: sourceUserId,
        // target transfer instructions
        group_bookmarks: {
          // indicate explicit transfer
          transfer_ownership: 1,
          // require: target user (optional on backend, defaults to current user)
          transfer_user_id: targetUserId
        }
      };
    } else { // default
      return {
        user_id: sourceUserId
      };
    }
  }

  private normalizeCollectionModel(collection: CollectionModel): CollectionModel {
    if (collection.collection_content) {
      collection.collection_content.forEach((bookmark) => {
        this.bookmarkService.normalizeBookmarkModel(bookmark);
      });
    }
    return collection;
  }

  // TODO: centralize form posting logic or migrate service to accept JSON
  private formPost<T>(url: string, payload: Object): Observable<T> {
    const formHeaders = {
      headers: new HttpHeaders({
        'Content-Type':  'application/x-www-form-urlencoded'
      })
    };
    const body = new URLSearchParams();
    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      const encodedValue = `${value instanceof Object ? JSON.stringify(value) : value}`;
      body.set(key, encodedValue);
    });
    return this.http.post(`${url}`, body.toString(), formHeaders)
      .pipe(
        map((reponse: T) => reponse)
      );
  }

}
